import classnames from 'classnames'
import { BadgeItem } from 'embed/components/Badges/Badge'
import { BadgeList } from 'embed/components/Badges/container'
import SpecialSubtypeNotice from 'embed/components/SpecialSubtypeNotice'
import { COMMON_CREDIT_CARD_PURPOSES, CreditCardSubtypes } from 'embed/constants'
import { willRedirectToExternalOfferPage, willSelectCreditCardOffer } from 'embed/constants/config'
import { formatImageUrl, getContrastingColor } from 'embed/utils'
import { CreditOffer } from 'embed/constants/models'
import { css } from 'emotion'
import { CreditCardFlow, CreditCardStep } from 'even-ts-static'
import * as React from 'react'

import { CCIntroVsPreviewVsHybridOfferList, withExperiment } from 'embed/experiments'
import { MultiProductState } from 'embed/reducers'
import { hybridOfferListSelector } from 'embed/selectors/ui'
import { connect } from 'react-redux'
import { useTheme } from 'ui/multi-product/hooks'

const commonStyles = require('embed/styles/results')
const styles = require('./styles.scss')

// Special subtype notice (e.g. secured card)
const SpecialSubtypeNoticeContainer: React.FC<{ productSubType?: string }> = ({ productSubType }) => {
  const { colorTextSecondary } = useTheme()
  if (productSubType !== CreditCardSubtypes.SecuredCard) {
    return null
  }
  return (
    <div
      className={classnames(
        styles.specialSubtypeNotice,
        css`
          color: ${colorTextSecondary};
        `,
      )}
    >
      <SpecialSubtypeNotice subtype={productSubType} />
    </div>
  )
}

export interface RatesLinkProps {
  ratesUrl?: string
}

const RatesLink: React.FC<RatesLinkProps> = ({ ratesUrl }) => {
  const { colorPrimary } = useTheme()
  if (!ratesUrl) {
    return null
  }
  return (
    <div className={classnames(styles.linkContainer)}>
      <a
        className={classnames(
          styles.linkText,
          css`
            color: ${colorPrimary};
          `,
        )}
        href={ratesUrl}
        target="_blank"
      >
        See all rates and terms
      </a>
    </div>
  )
}

const getCardBadges = (cardPurposes): BadgeItem[] =>
  COMMON_CREDIT_CARD_PURPOSES.map(
    (purpose) => cardPurposes?.find((existingPurpose) => existingPurpose === purpose.value) && { label: purpose.label },
  ).filter((cardBadge) => !!cardBadge) ?? []

export interface DefaultCreditListItemProps {
  applyNow: boolean | undefined
  dispatchCreditCardOfferCta: (offer: CreditOffer) => void
  dispatchCreditCardOfferCtaSecondary: (offer: CreditOffer) => void
  flow: CreditCardFlow | undefined
  isHybridOfferListEnabled?: boolean
  offer: CreditOffer
  selectedVariantId?: string
  showFooter: boolean
  step: CreditCardStep | undefined
}

const CreditListItemImage: React.FC<DefaultCreditListItemProps> = ({
  applyNow,
  flow,
  isHybridOfferListEnabled,
  dispatchCreditCardOfferCta,
  dispatchCreditCardOfferCtaSecondary,
  offer,
  selectedVariantId,
  showFooter,
  step,
}) => {
  const { backdropStyles } = useTheme()

  const { cardImageUrl, cardName, cardPurposes, ratesUrl } = offer.details

  const callToAction = () => {
    const { borderRadiusSmall, ctaStyles, colorTextPrimary, colorTextSecondary, fontFamilyPrimary } = useTheme()
    const {
      details: { preApproved, preQualified, preSelected },
      partner: { name: issuerName, subtextOverride, supportsPersonalizedOffers, supportsPreSelect } = {},
    } = offer

    // Call-to-action copy
    let callToActionButtonCopy = 'Continue'
    let callToActionLabelCopy

    const isPreviewExperiment =
      selectedVariantId === CCIntroVsPreviewVsHybridOfferList.variants.Preview.id && step === CreditCardStep.Preview

    if (
      applyNow ||
      isPreviewExperiment ||
      willRedirectToExternalOfferPage(!!supportsPersonalizedOffers, !!supportsPreSelect, flow)
    ) {
      // If we are redirecting to an external offer page
      const callToActionLabelText = subtextOverride ?? issuerName
      callToActionLabelCopy = `on ${callToActionLabelText}'s site`
      callToActionButtonCopy = 'Apply Now'
    } else if (willSelectCreditCardOffer(!!supportsPersonalizedOffers, !!supportsPreSelect, flow)) {
      // Sending to the pre-qual/pre-select form
      callToActionLabelCopy =
        (preApproved || preQualified || preSelected) && (step === CreditCardStep.Preview || !step)
          ? ''
          : 'See if you may qualify'

      callToActionButtonCopy = 'Get Matched'
    }

    if (isHybridOfferListEnabled) {
      callToActionButtonCopy = 'See if I qualify'
      const callToActionLabelText = issuerName
      callToActionLabelCopy = `on ${callToActionLabelText}'s site`
    }

    if (step === CreditCardStep.Results) {
      callToActionButtonCopy = 'Apply Now'
    }

    const hidePrimaryCTA = isHybridOfferListEnabled && !supportsPreSelect

    return (
      <>
        {!hidePrimaryCTA && (
          <button
            data-test-id="offer_continue"
            className={classnames(
              commonStyles.button,
              styles.ctaButton,
              css(`
                border-radius: ${borderRadiusSmall};
                color: ${getContrastingColor(ctaStyles?.backgroundColor, { black: colorTextPrimary })};
                &:hover {opacity: 0.84;}
              `),
            )}
            style={ctaStyles}
            onClick={creditOfferCta}
          >
            {callToActionButtonCopy}
          </button>
        )}
        {isHybridOfferListEnabled && (
          <button
            data-test-id="offer_continue"
            className={classnames(
              commonStyles.button,
              styles.ctaButton,
              css(`
                  border: 2px solid ${ctaStyles?.backgroundColor};
                  background-color: #fff;
                  border-radius: ${borderRadiusSmall};
                  color: ${ctaStyles?.backgroundColor};
                  font-family: ${fontFamilyPrimary};
                  &:hover {opacity: 0.84;}
                `),
            )}
            onClick={creditOfferCtaSecondary}
          >
            Apply Now
          </button>
        )}
        <div
          data-test-id="ctaButton"
          className={classnames(
            styles.ctaNote,
            css`
              color: ${colorTextSecondary};
            `,
          )}
        >
          <span className={classnames(styles.callToActionLabelCopy)}>{callToActionLabelCopy}</span>
        </div>
      </>
    )
  }

  const creditOfferCta = React.useCallback(
    (event: React.MouseEvent<Element>) => {
      // Prevent multiple windows from opening on offer click due to multiple click targets
      event.stopPropagation()
      dispatchCreditCardOfferCta(offer)
    },
    [dispatchCreditCardOfferCta, offer],
  )

  const creditOfferCtaSecondary = React.useCallback(
    (event: React.MouseEvent<Element>) => {
      // Prevent multiple windows from opening on offer click due to multiple click targets
      event.stopPropagation()
      dispatchCreditCardOfferCtaSecondary(offer)
    },
    [dispatchCreditCardOfferCtaSecondary, offer],
  )

  const cardImage = () => {
    const imgSrc = formatImageUrl(cardImageUrl)

    return (
      <div>
        <img alt={cardName} className={styles.cardImage} onClick={creditOfferCta} src={imgSrc} />
      </div>
    )
  }

  const cardBadges = getCardBadges(cardPurposes)

  return (
    <div className={styles.creditListItemImageContainer}>
      {cardImage()}
      <SpecialSubtypeNoticeContainer productSubType={offer.productSubType} />
      <div className={styles.cardNameContainer}>
        <span className={styles.cardName}>{cardName}</span>
        <div className={styles.badgesList}>
          <BadgeList header="Good for" items={cardBadges} />
        </div>
      </div>
      {callToAction()}
      <RatesLink ratesUrl={ratesUrl} />
      {showFooter && (
        <div
          className={classnames(
            styles.footer,
            css`
              background-color: ${backdropStyles.backgroundColor};
            `,
          )}
        />
      )}
    </div>
  )
}

const mapStateToProps = (state: MultiProductState) => ({
  isHybridOfferListEnabled: hybridOfferListSelector(state),
})

export const CreditListItemImageContainer = withExperiment(CCIntroVsPreviewVsHybridOfferList)(
  connect(mapStateToProps)(CreditListItemImage),
)
